import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped } from './dedupedFetch';
import type { RightNowPost } from '../types/right_now.types';
import type { Event } from '../types/event.types';
import type { PublicEventMapPin } from '../types/public_event.types';
import type { NearbyUser } from '../types/user.types';

export interface ReverseGeocodeResult {
  address: string;
  placeId: string | null;
}

/**
 * Phase D1 of YC demo + closed-beta build (ADR-023). Room pins surfaced on the
 * Map for the requesting user. Server-side visibility filter excludes
 * alternates and isolates demo-sandbox sessions from real-user pins.
 */
export interface RoomMapPin {
  id:                  string;
  activityLabel:       string;
  activityType:        string;
  /** ISO 8601 — start of the Room time window. */
  timeStart:           string;
  locationName:        string | null;
  lat:                 number;
  lng:                 number;
  status:              'screening' | 'confirmed';
  preferredGroupSize:  number;
  /** Set on confirmed rooms — drives the "Open Room" CTA target. */
  eventId:             string | null;
  /** TRUE when the requesting user organized the Room. */
  isOrganizer:         boolean;
  /** Room format. Remote rooms render in the orbit view, not as a map pin. */
  format:              'in_person' | 'remote';
  /** Zoom join URL — only on confirmed remote rooms. */
  zoomUrl:             string | null;
}

/** Aggregated cluster pin for a fallback city — only present in the demo
 *  Map response. Real users never receive these; the controller short-
 *  circuits before the query. Drives the global-clusters affordance so a
 *  demo viewer panning the map sees activity outside their own city. */
export interface DemoCityCluster {
  city:        string;
  lat:         number;
  lng:         number;
  cohortCount: number;
  eventCount:  number;
}

export interface NearbyAllResult {
  rightNow: RightNowPost[];
  events: Event[];
  publicEvents: PublicEventMapPin[];
  /** Phase D1: confirmed/screening rooms the requesting user is invited to or organizing. */
  myRooms?: RoomMapPin[];
  /** Cohort addendum: demo-only globally-visible clusters of cohort users +
   *  seeded events in the 9 DEMO_FALLBACK_CITIES other than the viewer's
   *  own. Absent for real users. */
  demoCityClusters?: DemoCityCluster[];
}

export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<ReverseGeocodeResult> {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  const res = await apiClient.get<{ success: true; data: ReverseGeocodeResult }>(
    `${API.GEO_REVERSE}?${params}`,
  );
  return res.data.data;
}

export async function getNearbyAll(
  lat: number,
  lng: number,
  radiusMiles?: number,
): Promise<NearbyAllResult> {
  const qLat = lat.toFixed(3);
  const qLng = lng.toFixed(3);
  const qR = radiusMiles ?? 'def';
  // 10s TTL — Right Now feed and Map pins should feel near-live but the
  // brief window absorbs Horizon's tab-cycle bursts.
  return deduped(`geo:nearbyAll:${qLat}:${qLng}:${qR}`, async () => {
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
    if (radiusMiles) params.set('radiusMiles', String(radiusMiles));
    const res = await apiClient.get<{ success: true; data: NearbyAllResult }>(
      `${API.GEO_NEARBY_ALL}?${params}`,
    );
    return res.data.data;
  }, 10_000);
}

// ── Nearby Users ───────────────────────────────────────────────────────────

export async function getNearbyUsers(
  lat: number,
  lng: number,
  radiusMiles?: number,
): Promise<NearbyUser[]> {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  if (radiusMiles) params.set('radiusMiles', String(radiusMiles));
  const res = await apiClient.get<{ success: true; data: NearbyUser[] }>(
    `${API.GEO_NEARBY_USERS}?${params}`,
  );
  return res.data.data;
}

// ── Place Search ────────────────────────────────────────────────────────────

export interface PlaceSearchResult {
  id: string;
  googlePlaceId: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  category: string | null;
}

/** Nearby places by pure proximity — no keyword bias. Excludes residences. */
export async function getNearbyPlaces(
  lat: number,
  lng: number,
  radiusMeters = 500,
): Promise<PlaceSearchResult[]> {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng), radius: String(radiusMeters) });
  const res = await apiClient.get<{ success: true; data: PlaceSearchResult[] }>(
    `/geo/nearby-places?${params}`,
  );
  return res.data.data;
}

export async function searchPlaces(
  query: string,
  lat?: number,
  lng?: number,
): Promise<PlaceSearchResult[]> {
  const params = new URLSearchParams({ query });
  if (lat != null) params.set('lat', String(lat));
  if (lng != null) params.set('lng', String(lng));
  const res = await apiClient.get<{ success: true; data: PlaceSearchResult[] }>(
    `${API.GEO_PLACE_SEARCH}?${params}`,
  );
  return res.data.data;
}

// ── Place Tally ─────────────────────────────────────────────────────────────

export interface PlaceTallyItem {
  placeId: string;
  googlePlaceId: string;
  placeName: string;
  placeAddress: string | null;
  activePostCount: number;
}

export async function getPlaceTally(placeIds?: string[]): Promise<PlaceTallyItem[]> {
  const params = new URLSearchParams();
  if (placeIds && placeIds.length > 0) params.set('placeIds', placeIds.join(','));
  const query = params.toString();
  const res = await apiClient.get<{ success: true; data: PlaceTallyItem[] }>(
    `${API.GEO_PLACE_TALLY}${query ? `?${query}` : ''}`,
  );
  return res.data.data;
}
